(ns ui.subscription.credits-modal
  "Modal dialog shown when a user has run out of credits and tries to use
   a feature that requires credits (e.g., story posting).

   Trial users (no current or former subscription) get subscribe framing
   with pricing and the welcome discount — this is the moment their trial
   ends. Paying customers get the add-credits framing."
  (:require [app.client-log :as client-log]
            [parts.replicant.command :as command]
            [parts.replicant.query :as query]
            [storrito.calendly.links :as calendly]
            [storrito.ui.button :as button]
            [ui.subscription.core :as core]))

(defn- questions-footer
  [session]
  [:div.mt-6.text-center.text-xs.text-gray-500
   [:p
    "Questions? "
    [:button.text-blue-600.font-medium
     {:class ["hover:text-blue-700"]
      :on {:click [[:crisp/open-chat]]}}
     "Chat with us"]
    " or "
    [:a.text-blue-600.hover:text-blue-700.font-medium
     {:href (calendly/url :solutions "credits-modal" (:organization/uuid session))
      :target "_blank"}
     "jump in a call"]
    "."]])

(defn- price-row [label price]
  [:div.flex.items-baseline.justify-between.p-3.rounded-lg.bg-gray-50
   [:span.text-sm.text-gray-600 label]
   [:span.text-lg.font-bold.text-gray-900
    price
    [:span.text-sm.font-normal.text-gray-500 "/mo with discount"]]])

(defn- log-modal-shown!
  "Funnel event with the audience variant. Only called once the session
   is loaded, so a body swap from the loading fallback to the trial
   framing cannot record a wrong variant."
  [variant]
  (fn [_]
    (client-log/log! :subscription/credits-exhausted-modal
                     {:modal/variant variant})))

(defn- trial-ended-body
  [session]
  [:div.flex.flex-col.items-center
   {:replicant/on-mount (log-modal-shown! "trial")}
   [:div.mx-auto.flex.size-16.items-center.justify-center.rounded-full.bg-amber-100.mb-6
    [:i.fas.fa-coins.text-amber-600.text-2xl]]

   [:h2.text-xl.font-semibold.text-gray-900.mb-3
    (if-let [amount (:free-plan.trial/start-credit-amount-formatted session)]
      (str "Your " amount " free credit is used up")
      "Your free credit is used up")]
   [:p.text-gray-600.text-sm.leading-relaxed.mb-6
    "You've seen Storrito work. Subscribe to keep posting — your accounts
     and media stay exactly as they are."]

   [:div.w-full.max-w-sm
    [:div.space-y-3.mb-4
     (price-row "1 Instagram account" "~$15")
     (price-row "3 Instagram accounts" "~$38")]
    [:p.text-xs.text-gray-500.text-center.mb-6
     "Pay-as-you-go with your 25% welcome discount on connections.
      Only charged while accounts are connected. Cancel anytime."]

    [:ui/a
     {:ui/location {:location/page-id :pages/subscription
                    :location/query-params {:source "credits-modal"}}
      :class (concat button/button-primary-css
                     ["w-full"])}
     [:i.fas.fa-paper-plane.mr-2]
     "Keep posting — subscribe"]]

   (questions-footer session)])

(defn- add-credits-body
  [state session]
  [:div.flex.flex-col.items-center
   (when (some? session)
     {:replicant/on-mount (log-modal-shown! "add-credits")})
   [:div.mx-auto.flex.size-16.items-center.justify-center.rounded-full.bg-amber-100.mb-6
    [:i.fas.fa-coins.text-amber-600.text-2xl]]

   [:h2.text-xl.font-semibold.text-gray-900.mb-3
    "You've Run Out of Credits"]
   [:p.text-gray-600.text-sm.leading-relaxed.mb-6
    "Your credit balance has reached zero. To continue using Storrito's services, please add more credits to your account."]

   [:div
    [:div.space-y-3.mb-8
     [:div.flex.items-start.gap-3
      [:div.flex-shrink-0.w-6.h-6.rounded-full.bg-blue-100.flex.items-center.justify-center.mt-0.5
       [:i.fas.fa-bolt.text-blue-600.text-xs]]
      [:div.text-left
       [:p.font-medium.text-gray-900.text-sm "Instant activation"]
       [:p.text-xs.text-gray-500 "Credits are available immediately after purchase"]]]
     [:div.flex.items-start.gap-3
      [:div.flex-shrink-0.w-6.h-6.rounded-full.bg-blue-100.flex.items-center.justify-center.mt-0.5
       [:i.fas.fa-undo.text-blue-600.text-xs]]
      [:div.text-left
       [:p.font-medium.text-gray-900.text-sm "Unused credits roll over"]
       [:p.text-xs.text-gray-500 "Your credits never expire"]]]]

    [:ui/a
     {:ui/location {:location/page-id :pages/subscription
                    :location/query-params {:source "credits-modal"}}
      :class (concat button/button-primary-css
                     ["w-full"])}
     [:i.fas.fa-plus-circle.mr-2]
     "Add Credits"]]

   (questions-footer session)])

(defn trial-user?
  "True when the session belongs to a user who never had a subscription —
   the credits that ran out were their trial start credits."
  [session]
  (boolean
    (and session
         (not (:subscription-id session))
         (not (:subscription-canceled session))
         (not (:organization/legacy-subscription-id session))
         (not (:organization/legacy-customer? session)))))

(defn render
  "The modal content explaining that credits have been used up."
  [state]
  (let [session (query/get-result state core/user-session-query)]
    (if (trial-user? session)
      (trial-ended-body session)
      (add-credits-body state session))))

(def modal
  "Replicant modal definition. Register this in ui.register/register.

   Open together with a user-session query so the trial/customer framing
   can be decided:

   Usage from Replicant:
   {:on {:click [[:data/query {:query/kind :query/user-session}]
                 [:ui/modal-open :modals/credits-exhausted {}]]}}"
  {:modal-id :modals/credits-exhausted
   :render #'render})
